import React, { useState, useEffect, useRef } from 'react';
import { FaRegHeart, FaChevronDown, FaBoxOpen } from 'react-icons/fa';
import { RiShoppingCartLine } from 'react-icons/ri';
import { MdOutlinePlaylistAdd } from 'react-icons/md';
import Rating from '../Rating';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { add_to_card, add_to_wishlist, messageClear } from '../../store/reducers/cardReducer';
import { add_to_my_list, messageClear as myListMessageClear } from '../../store/reducers/myListReducer';
import { get_products, query_products, price_range_product } from '../../store/reducers/homeReducer';
import toast from 'react-hot-toast';
import Categorys from '../Categorys';
import Container from '../Container';
import { useCurrency } from '../../context/CurrencyContext';

const FeatureProducts = ({ products }) => {
  const { formatBase } = useCurrency();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector(state => state.auth);
  const { errorMessage, successMessage } = useSelector(state => state.card);
  const { successMessage: myListSuccessMessage, errorMessage: myListErrorMessage } = useSelector(state => state.myList);
  const { priceRange, totalProduct } = useSelector(state => state.home);

  const INITIAL_VISIBLE = 25;
  const LOAD_STEP = 15;
  const MID_BANNER_AT = 25;

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categoryPage, setCategoryPage] = useState(1);
  const requestedPageRef = useRef(1);

  // Backend needs a price range to return results at all — fetch it once.
  useEffect(() => {
    dispatch(price_range_product());
  }, [dispatch]);

  // Query the backend for the selected category (client-side data only ever holds
  // the small curated homepage set, so filtering it locally silently returned nothing).
  useEffect(() => {
    if (!selectedCategory) return;
    requestedPageRef.current = 1;
    setCategoryPage(1);
    dispatch(query_products({
      category: selectedCategory,
      low: priceRange.low,
      high: priceRange.high,
      pageNumber: 1,
    }));
  }, [selectedCategory, priceRange.low, priceRange.high, dispatch]);

  // Page 1 replaces the accumulator, later pages (from "See more") append to it.
  useEffect(() => {
    if (!selectedCategory) return;
    setCategoryProducts(prev => (requestedPageRef.current === 1 ? products : [...prev, ...products]));
  }, [products, selectedCategory]);

  const displayedProducts = selectedCategory ? categoryProducts : products;
  const visibleProducts = selectedCategory ? displayedProducts : displayedProducts.slice(0, visibleCount);
  const hasMore = selectedCategory
    ? displayedProducts.length < totalProduct
    : visibleCount < displayedProducts.length;
  const showMidBanner = visibleCount > MID_BANNER_AT && displayedProducts.length > MID_BANNER_AT;

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    setVisibleCount(INITIAL_VISIBLE);
    setCategoryProducts([]);
    if (!cat) dispatch(get_products());
  };

  const handleShowMore = () => {
    if (selectedCategory) {
      const nextPage = categoryPage + 1;
      requestedPageRef.current = nextPage;
      setCategoryPage(nextPage);
      dispatch(query_products({
        category: selectedCategory,
        low: priceRange.low,
        high: priceRange.high,
        pageNumber: nextPage,
      }));
    } else {
      setVisibleCount(prev => Math.min(prev + LOAD_STEP, products.length));
    }
  };

  const add_card = (id) => {
    if (userInfo) {
      dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: id }));
    } else {
      navigate('/login');
    }
  };

  const add_wishlist = (pro) => {
    if (!userInfo) { navigate('/login'); return; }
    dispatch(add_to_wishlist({
      userId: userInfo.id,
      productId: pro._id,
      name: pro.name,
      price: pro.price,
      image: pro.images[0],
      discount: pro.discount,
      rating: pro.rating,
      slug: pro.slug,
    }));
  };

  const add_my_list = (pro) => {
    if (!userInfo) { navigate('/login'); return; }
    dispatch(add_to_my_list({ userId: userInfo.id, productId: pro._id }));
  };

  const finalPrice = (price, discount) =>
    discount ? price - (price * discount) / 100 : price;

  useEffect(() => {
    if (successMessage) { toast.success(successMessage); dispatch(messageClear()); }
    if (errorMessage)   { toast.error(errorMessage);   dispatch(messageClear()); }
  }, [successMessage, errorMessage, dispatch]);

  useEffect(() => {
    if (myListSuccessMessage) { toast.success(myListSuccessMessage); dispatch(myListMessageClear()); }
    if (myListErrorMessage)   { toast.error(myListErrorMessage);   dispatch(myListMessageClear()); }
  }, [myListSuccessMessage, myListErrorMessage, dispatch]);

  const renderGrid = (items) => (
    <div className="grid grid-cols-5 lg:grid-cols-4 md-lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2">
      {items.map((p, i) => {
        const price = finalPrice(p.price, p.discount);
        return (
          <div key={i} className="group p-2">
            <Link to={`/product/details/${p.slug}`} target="_blank" rel="noopener noreferrer" className="relative block overflow-hidden bg-gray-50">
              <div className="w-full aspect-square overflow-hidden">
                <img
                  src={p.images[0]}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              <div className="absolute right-2 top-2 flex flex-col gap-1.5 opacity-0 transition-all duration-150 group-hover:opacity-100">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); add_wishlist(p); }}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-gray-400 shadow-sm transition-all duration-150 hover:bg-white hover:text-[#E21C1B]"
                  title="Add to Wishlist"
                >
                  <FaRegHeart size={10} />
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); add_my_list(p); }}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-gray-400 shadow-sm transition-all duration-150 hover:bg-white hover:text-[#1769e0]"
                  title="Add to My List"
                >
                  <MdOutlinePlaylistAdd size={14} />
                </button>
              </div>
            </Link>

            <div className="pt-2">
              <Link to={`/product/details/${p.slug}`} target="_blank" rel="noopener noreferrer">
                <h2 className="line-clamp-1 text-[12px] text-gray-600">{p.name}</h2>
              </Link>

              <div className="mt-1 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-baseline gap-1.5">
                  <span className="text-[15px] font-bold text-[#E21C1B]">{formatBase(price)}</span>
                  {p.discount > 0 && (
                    <span className="truncate text-[11px] text-gray-400 line-through">{formatBase(p.price)}</span>
                  )}
                </div>
                <button
                  onClick={() => add_card(p._id)}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-[#E21C1B] hover:text-white"
                  title="Add to Cart"
                >
                  <RiShoppingCartLine size={12} />
                </button>
              </div>

              {p.rating > 0 && (
                <div className="mt-1 flex items-center gap-1">
                  <div className="flex text-[9px]"><Rating ratings={p.rating} /></div>
                  <span className="text-[10px] text-gray-400">{p.rating.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="w-full">
      <Container>

        {/* SECTION HEADING BANNER */}
        <div className="mb-4 overflow-hidden rounded-xl">
        <img
          src="/images/banner/Trending products at unbeatable prices.png"
          alt="Trending products at unbeatable prices"
          className="w-full h-[100px] md:h-[120px] lg:h-[140px] object-cover"
        />
      </div>

        {/* CATEGORY INTEREST SECTION */}
        <div className="mb-5">
          {/* Heading */}
          <div className="text-center mb-4">
            <p className="text-[12.5px] font-bold tracking-[0.18em] text-[#E21C1B] uppercase mb-1">
              ★&nbsp;&nbsp;Shop by Category&nbsp;&nbsp;★
            </p>
            <h3 className="text-[22px] font-black text-gray-900 uppercase tracking-tight leading-none">
              Explore Your Interests
            </h3>
          </div>

          {/* Pill slider */}
          <Categorys selectedCategory={selectedCategory} onSelect={handleCategorySelect} />
        </div>

        {/* PRODUCT GRID */}
        {visibleProducts.length > 0 ? (
          renderGrid(visibleProducts)
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <FaBoxOpen size={36} className="mb-3" />
            <p className="text-sm font-semibold text-gray-600">No products in this category yet</p>
            <p className="text-xs text-gray-400 mt-1">Check back soon or explore another category</p>
          </div>
        )}

        {/* MID PROMO BANNER — revealed after first 25 products load */}
        {showMidBanner && (
          <div className="my-8 bg-gradient-to-r from-[#E21C1B] to-[#ff6b35] rounded-2xl p-8 text-white text-center shadow-md">
            <p className="text-xs font-semibold uppercase tracking-widest opacity-80 mb-1">Limited Time</p>
            <h2 className="text-2xl font-extrabold">Mid Season Mega Sale 🔥</h2>
            <p className="mt-1 text-sm opacity-90">Grab the best deals before they're gone</p>
            <Link
              to="/products"
              className="inline-block mt-4 px-7 py-2.5 bg-white text-[#E21C1B] font-bold rounded-full text-sm hover:bg-gray-100 transition"
            >
              Shop Now →
            </Link>
          </div>
        )}

        {/* SEE MORE BUTTON */}
        {hasMore && (
          <div className="flex justify-center mt-8 mb-2">
            <button
              onClick={handleShowMore}
              className="flex items-center gap-2.5 px-10 py-[11px] bg-[#E21C1B] text-white text-sm font-semibold rounded-full shadow-md hover:bg-[#c01717] active:scale-95 transition-all duration-150"
            >
              See more <FaChevronDown size={12} />
            </button>
          </div>
        )}

      </Container>
    </div>
  );
};

export default FeatureProducts;
